import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  LayoutDashboard,
  Users,
  CalendarDays,
  Activity,
  Settings as SettingsIcon,
  LogOut,
  Menu,
  X,
} from 'lucide-react';
import { getUser, logout, type SwaUser } from '../lib/auth';
import { Logo } from './Logo';
import { TopProgressBar } from './ui';
import { ImpersonationBar } from './ImpersonationBar';
import { ThemeToggle } from './ThemeToggle';

const nav = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/leads', label: 'Leads', icon: Users },
  { to: '/rdv', label: 'Rendez-vous', icon: CalendarDays },
  { to: '/supervision', label: 'Supervision', icon: Activity },
  { to: '/settings', label: 'Paramètres', icon: SettingsIcon },
];

function initialsOf(email?: string): string {
  if (!email) return '··';
  const local = email.split('@')[0] ?? email;
  const parts = local.split(/[._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return local.slice(0, 2).toUpperCase();
}

function SidebarContent({ onItemClick }: { onItemClick?: () => void }) {
  const [user, setUser] = useState<SwaUser | null>(null);
  useEffect(() => {
    getUser().then(setUser);
  }, []);

  return (
    <div className="flex h-full flex-col">
      {/* Bloc logo : halo dore subtil derriere pour identite, fonctionne en dark et light. */}
      <div className="relative shrink-0 px-6 pb-6 pt-6">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-10 left-1/2 h-32 w-32 -translate-x-1/2 rounded-full bg-accent-gold/15 blur-2xl" />
        </div>
        <Link to="/dashboard" className="relative block" aria-label="Stella Cars — accueil">
          <Logo variant="full" className="h-10 w-auto" animated />
        </Link>
        <div className="relative mt-3 flex items-center gap-2">
          <span className="h-px w-5 bg-gradient-to-r from-transparent to-accent-gold/70" />
          <span className="font-display text-[11px] italic tracking-wide text-accent-gold/90">
            Back-office expert
          </span>
          <span className="h-px flex-1 bg-gradient-to-r from-accent-gold/30 to-transparent" />
        </div>
      </div>

      <div className="divider-gold mx-6 shrink-0" />

      <div className="mt-3 flex shrink-0 justify-center px-3">
        <ImpersonationBar user={user} />
      </div>

      {/* Zone scrollable uniquement si la nav depasse en hauteur */}
      <nav className="mt-4 flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-3">
        {nav.map(({ to, label, icon: Icon }, idx) => (
          <motion.div
            key={to}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.05 + idx * 0.04, duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <NavLink
              to={to}
              onClick={onItemClick}
              className={({ isActive }) =>
                `group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition ${
                  isActive
                    ? 'text-accent-gold'
                    : 'text-cream/75 hover:bg-white/5 hover:text-cream'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <motion.span
                      layoutId="sidebar-active-pill"
                      className="absolute inset-0 -z-10 rounded-lg bg-gradient-to-r from-accent-gold/15 via-accent-gold/10 to-transparent ring-1 ring-accent-gold/25"
                      transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                    />
                  )}
                  {isActive && (
                    <span className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-full bg-gradient-to-b from-accent-goldLight to-accent-gold shadow-gold" />
                  )}
                  <Icon
                    size={18}
                    className={`shrink-0 transition ${
                      isActive ? 'text-accent-gold' : 'group-hover:text-accent-gold/90'
                    }`}
                  />
                  <span className="font-medium">{label}</span>
                </>
              )}
            </NavLink>
          </motion.div>
        ))}
      </nav>

      <div className="shrink-0 px-4 pb-4 pt-2">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.4 }}
          className="glass flex items-center gap-3 px-3 py-3"
        >
          <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-gradient-to-br from-accent-gold to-accent-goldLight text-[12px] font-bold text-primary-bg shadow-gold">
            {initialsOf(user?.userDetails)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[13px] font-medium text-cream">
              {user?.userDetails ?? 'Expert Stella'}
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-dark">
              {user?.userRoles?.includes('admin') ? 'Administrateur' : 'Connecté'}
            </div>
          </div>
          <button
            type="button"
            onClick={logout}
            aria-label="Se déconnecter"
            title="Se déconnecter"
            className="grid h-8 w-8 place-items-center rounded-md text-cream/70 transition hover:bg-white/5 hover:text-error"
          >
            <LogOut size={15} />
          </button>
          <ThemeToggle />
        </motion.div>
        <div className="mt-3 text-center text-[10px] text-muted-dark/70">
          v1.0 · © {new Date().getFullYear()} Stella Cars
        </div>
      </div>
    </div>
  );
}

export function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <div className="relative flex min-h-screen">
      {/* Barre de progression globale (queries + mutations) */}
      <TopProgressBar />

      {/* Ambient orbs en fond */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="orb absolute -left-32 top-20 h-96 w-96 rounded-full bg-accent-gold/10 blur-[120px]" />
        <div className="orb-slow absolute -right-40 top-1/2 h-[28rem] w-[28rem] rounded-full bg-info/10 blur-[140px]" />
      </div>

      {/* Sidebar desktop : sticky pleine hauteur, footer (compte) collé en bas, contenu central scrollable si overflow. */}
      <aside
        className="sidebar sticky top-0 hidden h-screen w-64 shrink-0 border-r backdrop-blur-xl lg:block"
        style={{ backgroundColor: 'var(--sidebar-bg)', borderRightColor: 'var(--sidebar-border)' }}
      >
        <SidebarContent />
      </aside>

      {/* Topbar mobile */}
      <div className="fixed inset-x-0 top-0 z-30 flex items-center justify-between border-b border-white/5 bg-primary-bg/80 px-4 py-3 backdrop-blur-xl lg:hidden">
        <Link to="/dashboard" aria-label="Stella Cars — accueil">
          <Logo variant="full" className="h-8 w-auto" />
        </Link>
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          aria-label="Ouvrir le menu"
          className="grid h-9 w-9 place-items-center rounded-md border border-white/10 bg-white/5 text-cream hover:border-accent-gold/30"
        >
          <Menu size={18} />
        </button>
      </div>

      {/* Drawer mobile */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-primary-bg/70 backdrop-blur-sm lg:hidden"
              onClick={() => setMobileOpen(false)}
            />
            <motion.aside
              key="drawer"
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="sidebar fixed inset-y-0 left-0 z-50 w-72 border-r shadow-2xl lg:hidden"
              style={{ backgroundColor: 'var(--sidebar-bg)', borderRightColor: 'var(--sidebar-border)' }}
            >
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                aria-label="Fermer le menu"
                className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-md text-cream/80 hover:bg-white/5"
              >
                <X size={16} />
              </button>
              <SidebarContent onItemClick={() => setMobileOpen(false)} />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Contenu — transition de page (entrée seule, pour éviter les bugs d'Outlet + AnimatePresence mode=wait) */}
      <main className="min-w-0 flex-1 px-4 pb-10 pt-20 sm:px-6 lg:px-10 lg:pt-8">
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
        >
          <Outlet />
        </motion.div>
      </main>
    </div>
  );
}
